"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import ApiService from "services/apiService";
import { useTranslation } from "hooks/useTranslation";
import MediaHero from "components/MediaHero";
import { useAppSelector } from "appHooks";
import { authSelector } from "features/auth/auth.selectors";
import { clientSelector } from "features/client/client.selectors";
import UserHeaderComponent from "components/UserHeaderComponent";
import { apiHeadersSelector } from "features/selectors";
import { configSelector } from "features/config/config.selectors";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const Intro = () => {
  const t = useTranslation();
  const [isFetched, setIsFetched] = useState(false);
  const isFetching = useRef(false); // Prevent multiple calls
  const { data: authData } = useAppSelector(authSelector);
  const { data: client } = useAppSelector(clientSelector);
  const { locale } = useAppSelector(configSelector);
  const apiHeaders = useAppSelector(apiHeadersSelector);
  const [projectCount, setProjectCount] = useState("0");
  const [camerasCount, setCamerasCount] = useState("0");
  const [camera360Count, setCamera360Count] = useState("0");
  const [cameraImagesCount, setCameraImagesCount] = useState("0");
  const [currentIndex, setCurrentIndex] = useState(0);

  const token = authData.token;

  useEffect(() => {
    let dir = locale == "ar" ? "rtl" : "ltr";
    let lang = locale == "ar" ? "ar" : "en";
    document.querySelector("html")?.setAttribute("dir", dir);
    document.querySelector("html")?.setAttribute("lang", lang);
  }, []);

  const fetchData = async () => {
    if (isFetched || isFetching.current) return;
    isFetching.current = true; // Set flag to true before fetching
    try {
      const data = await ApiService({
        method: "get",
        endpoint: "/client/v1/intro",
        headers: {
          Authorization: "Bearer " + token,
        },
        apiHeaders,
      });
      setIsFetched(true);
      setProjectCount(data?.project_count);
      setCamerasCount(data?.cameras_count);
      setCamera360Count(data?.camera_360_count);
      setCameraImagesCount(data?.camera_images_count);
    } finally {
      isFetching.current = false;
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleNextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % client.urls.length);
  };

  const handlePrevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? client.urls.length - 1 : prevIndex - 1
    );
  };

  const formatNumber = (num: number) => ("0" + num).slice(-2);

  const IntroSkeleton = () => (
    <>
      <UserHeaderComponent showSkeleton={!isFetched} />
      <div className="intro-wrapper body-bg">
        <div
          style={{
            height: "100vh",
            width: "100vw",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <Skeleton height="100%" />
        </div>

        <div className="container flex-end" style={{ position: "relative" }}>
          <div className="intro-row res-pd-0">
            <div className="intro-block">
              <div className="body-content">
                <h1>
                  <Skeleton width={300} height={40} />
                </h1>
                <p>
                  <Skeleton count={3} />
                </p>
              </div>

              <div className="intro-content">
                <div className="intro-stats-wrapper">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="intro-stats">
                      <div className="intro-label">
                        <Skeleton width={80} />
                      </div>
                      <div className="intro-count">
                        <Skeleton width={40} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="intro-actions">
                <Skeleton width={150} height={30} className="discover-btn" />
                <Skeleton width={150} height={30} className="discover-btn" />
              </div>
            </div>

            <div className="intro-block">
              <div className="slider-nav-wrapper">
                <Skeleton width={100} height={30} />
                <Skeleton width={80} height={30} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Replace the loading condition
  if (!isFetched) return <IntroSkeleton />;

  return (
    <>
      <UserHeaderComponent />
      <div
        className="intro-wrapper body-bg"
        style={{
          position: "relative",
        }}
      >
        <MediaHero
          type={client.dashboard_type}
          media={client.urls}
          interval={10000}
          handleNextSlide={handleNextSlide}
        />
        <div className="container flex-end" style={{ position: "relative" }}>
          <div className="intro-row res-pd-0">
            <div className="intro-block">
              <div className="body-content">
                <h1>{locale === "en" ? client.name : client.name_ar}</h1>
                <p>
                  {locale === "en" ? client.description : client.description_ar}
                </p>
              </div>
              <div className="intro-content">
                <div className="intro-stats-wrapper">
                  <div className="intro-stats">
                    <div className="intro-label">{t("intro.project")}</div>
                    <div className="intro-count">{projectCount}</div>
                  </div>
                  <div className="intro-stats">
                    <div className="intro-label">{t("intro.cameras")}</div>
                    <div className="intro-count">{camerasCount}</div>
                  </div>
                  <div className="intro-stats">
                    <div className="intro-label">{t("intro.camera360")}</div>
                    <div className="intro-count">{camera360Count}</div>
                  </div>
                  <div className="intro-stats">
                    <div className="intro-label">{t("intro.gallery")}</div>
                    <div className="intro-count">{cameraImagesCount}</div>
                  </div>
                </div>
              </div>
              <div className="intro-actions">
                <Link href={`/${locale}/projects`} className="discover-btn">
                  {t("intro.discoverProjects")} <i className="i-icon-right"></i>
                </Link>
                <Link href={`/${locale}/regions`} className="discover-btn">
                  {t("intro.discoverCategories")}{" "}
                  <i className="i-icon-right"></i>
                </Link>
              </div>
            </div>
            {client.dashboard_type === "MultiImage" && (
              <div className="intro-block">
                <div className="slider-nav-wrapper">
                  <div className="slider-counter">
                    <div className="slider-selected">
                      {formatNumber(currentIndex + 1)}
                    </div>
                    <div className="slider-total">
                      {formatNumber(client.urls.length)}
                    </div>
                  </div>

                  <div className="slider-nav-block">
                    <i
                      className="s-icon left clickable-arrow"
                      onClick={handlePrevSlide}
                    ></i>
                    <i
                      className="s-icon right clickable-arrow"
                      onClick={handleNextSlide}
                    ></i>
                  </div>
                  <div className="slider-progress"></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Intro;
